import { CheckIcon } from "@phosphor-icons/react/dist/csr/Check";
import { QuestionIcon } from "@phosphor-icons/react/dist/csr/Question";
import { TableIcon } from "@phosphor-icons/react/dist/csr/Table";
import { WarningIcon } from "@phosphor-icons/react/dist/csr/Warning";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button, buttonVariants } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@volca/ui/components/ui/tooltip";
import { toast } from "@volca/ui/lib/toast";
import { cn } from "@volca/ui/lib/utils";
import { getErrorMessage } from "@/core/api/errors";
import { dayjs } from "@/core/utils/date";
import {
  STATEMENT_IDENTITY,
  useGetAiScriptStatement,
} from "@/payroll/ai-script/query";
import { statementRowKey } from "@/payroll/ai-script/statement/statement-renderer";
import { useCloseManagedPackThread } from "@/payroll/pack-chat/manager-query";
import { PackChatStatusMark } from "@/payroll/pack-chat/pack-chat-status-mark";
import {
  isGeneralPayPackContext,
  referenceParts,
} from "@/payroll/pack-chat/reference-label";
import { getStatementItemReferences } from "@/payroll/pack-chat/statement-items";
import type { IPackChatThreadSummary } from "@/payroll/pack-chat/types";
import { getPayBreakdownSheetPathForName } from "@/payroll/pay-breakdown/routes";

export const PACK_CHAT_CONVERSATION_COLUMN = "mx-auto w-full min-w-0 max-w-5xl";

/** The fixed manager header for one PayPack conversation. */
export const PackChatConversationHeader = ({
  businessId,
  periodStart,
  runId,
  thread,
  technicianName,
}: {
  businessId: string | null;
  periodStart: string;
  runId: string | null;
  thread: IPackChatThreadSummary;
  technicianName: string;
}) => {
  const close = useCloseManagedPackThread(businessId, periodStart);
  const currentRunId = runId ?? thread.ai_script_run_id;
  const isGeneral = isGeneralPayPackContext(thread.context);
  const packQuery = useGetAiScriptStatement(businessId, currentRunId, {
    identity: STATEMENT_IDENTITY.technician,
    technicianId: thread.technician.id,
    enabled: !isGeneral && Boolean(businessId && currentRunId),
  });
  const pack = packQuery.data?.statement;
  const packAmounts = useMemo(
    () =>
      new Map(
        getStatementItemReferences(pack).map((reference) => [
          statementRowKey(reference),
          reference.amount,
        ]),
      ),
    [pack],
  );

  const { path, label, value, valueIsFigure } = referenceParts(thread.context);
  const contextKey = statementRowKey(thread.context);
  const currentAmount = packAmounts.get(contextKey);
  const figureMissing =
    !isGeneral && Boolean(pack) && !packAmounts.has(contextKey);
  const amountChanged =
    currentAmount !== undefined &&
    Boolean(value) &&
    valueIsFigure &&
    currentAmount !== value;
  const subject = [path, label, value && valueIsFigure ? value : null]
    .filter(Boolean)
    .join(" · ");
  const messageLabel = `${thread.message_count} ${
    thread.message_count === 1 ? "message" : "messages"
  }`;
  const sheetName = thread.source_sheet?.name;
  const sheetHref = sheetName
    ? getPayBreakdownSheetPathForName(periodStart, sheetName)
    : null;
  const open = thread.status === "open";

  const closeThread = async () => {
    try {
      await close.mutateAsync(thread.id);
      toast.success("Thread closed");
    } catch (error) {
      toast.error("Thread not closed", {
        description: getErrorMessage(error),
      });
    }
  };

  return (
    <Stack
      gap={0}
      align="stretch"
      className="w-full shrink-0 border-b border-line px-5 py-4"
    >
      <Stack
        direction="row"
        align="start"
        justify="between"
        gap={4}
        className={cn("w-full", PACK_CHAT_CONVERSATION_COLUMN)}
      >
        <Stack gap={0} align="stretch" className="min-w-0">
          <Stack direction="row" align="center" gap={1} className="min-w-0">
            <Text
              variant="title"
              className="min-w-0 truncate"
              title={technicianName}
            >
              {technicianName}
            </Text>
            {sheetHref && sheetName && (
              <Tooltip>
                <TooltipTrigger render={<span className="inline-flex" />}>
                  {/* A link styled as an icon button: it navigates, so it
                      keeps the link role a rendered Button would replace. */}
                  <Link
                    to={sheetHref}
                    className={cn(
                      buttonVariants({ variant: "ghost", size: "icon-xs" }),
                      "text-subtle",
                    )}
                  >
                    <TableIcon className="size-4" aria-hidden />
                    <span className="sr-only">
                      Open {sheetName} in the Grid
                    </span>
                  </Link>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-xs">
                  Open the {sheetName} sheet in the Grid, where this pay was
                  worked out.
                </TooltipContent>
              </Tooltip>
            )}
          </Stack>

          <Stack
            direction="row"
            align="baseline"
            gap={0}
            className="min-w-0 flex-wrap gap-x-1.5"
          >
            <Text variant="body" className="min-w-0">
              <span className="text-subtle">{messageLabel}</span>
              {subject && (
                <span className="font-medium text-subtle">
                  {" · "}
                  {subject}
                </span>
              )}
              {amountChanged && (
                <Tooltip>
                  <TooltipTrigger
                    render={
                      <span
                        tabIndex={0}
                        className="cursor-pointer whitespace-nowrap text-subtle"
                      />
                    }
                  >
                    {" "}
                    when asked
                    <QuestionIcon
                      className="ml-1 inline size-3 align-[-0.125em]"
                      aria-hidden
                    />
                  </TooltipTrigger>
                  <TooltipContent side="bottom" className="max-w-sm">
                    {value} is what this figure read when the question was
                    asked. The pack now shows {currentAmount}.
                  </TooltipContent>
                </Tooltip>
              )}
            </Text>

            {figureMissing && (
              <Tooltip>
                <TooltipTrigger
                  render={<span tabIndex={0} className="shrink-0" />}
                >
                  <span className="cursor-pointer text-sm font-medium whitespace-nowrap text-warning">
                    <WarningIcon
                      className="mr-1 inline size-3.5 align-[-0.1875em]"
                      aria-hidden
                    />
                    No longer on this pack
                  </span>
                </TooltipTrigger>
                <TooltipContent side="bottom" className="max-w-sm">
                  This figure was on the pack when the question was asked. A
                  later run renamed or removed it, so the pack no longer has the
                  row. The question and its answers are unchanged.
                </TooltipContent>
              </Tooltip>
            )}
          </Stack>

          <Text variant="body" className="text-subtle">
            {dayjs(thread.updated_at).fromNow()}
          </Text>
        </Stack>

        <Stack gap={2} align="end" className="shrink-0">
          <PackChatStatusMark
            open={open}
            label={open ? "Open" : "Closed"}
            hasAiMessages={thread.has_ai_messages}
            needsManager={thread.needs_manager}
            responseOwner={thread.response_owner}
          />
          {open && (
            <Button
              variant="ghost"
              className="text-subtle"
              disabled={close.isPending}
              onClick={() => void closeThread()}
            >
              <CheckIcon data-icon="inline-start" aria-hidden />
              {close.isPending ? "Closing…" : "Close thread"}
            </Button>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};
